/* ==========================================================================
   RPG SHOP — FIT THE CARD TO THE SCREEN
   --------------------------------------------------------------------------
   The details card group was scaled with a hardcoded `transform: scale(0.7)`,
   which neither responded to the viewport nor shed the layout height the
   transform stopped painting (925px reserved for 647px painted = phantom
   scroll). This measures natural height, derives one factor for paint and
   layout, and applies it.

   V2 RULE — ONE SCALE FOR THE WHOLE SHOP, NOT ONE PER CARD PER TAB.
   V1 fitted whichever card+tab was on screen. Two consequences, both
   reported the same morning:
     - every tab switch re-measured a different pane height, so k and the
       shed margin recomputed and the card visibly re-scaled and re-centred
       on each switch ("weird layout change");
     - each product carried its own k, so browsing the den changed the type
       size from card to card ("product info shouldn't be different sizes
       between products").
   The fix is two-part. (1) Measurement is global: reveal every product
   section inside one muted, synchronous excursion (no paint can happen
   before the styles are restored) and measure each card with its DETAILS
   pane active - avail / the fleet's max is THE shop's scale, so browsing
   products never changes the type size. (2) Each card's layout height is
   LOCKED to its own details-pane natural (--rpg-card-nat), so switching
   tabs swaps content inside a constant stone frame; a pane taller than the
   frame scrolls inside the reading band behind the existing fade+chevron.
   ========================================================================== */
(function () {
  /* ------------------------------------------------------------------
     THE STAGE LAW. The den is one composition, drawn at 1728x1117 and
     rendered at the largest uniform factor that still fits the screen:

         s = min(vw / REF_W, vh / REF_H)

     Everything below derives from that single number. The card's scale is
     kd * s, the gallery's is fkd * s, and because both variables carry the
     full render scale, the type inside them paints at s times its reference
     size without anything else being told about it.

     The DESIGN scale (kd, fkd) is the second factor and it answers a
     different question: how much of its own design column does this card's
     content need? That one is content-driven, measured, and viewport
     INDEPENDENT - the stage pins each item's layout width, so a card that
     measured 904px of natural content at 1728 and 846px at 2592 (purely
     because its column was wider there) now measures the same at both.
     ------------------------------------------------------------------ */
  var REF_W = 1728, REF_H = 1117;

  /* THE FLEET MAXIMUM IS A DESIGN TOKEN, NOT A RUNTIME MEASUREMENT.
     ------------------------------------------------------------------
     kd used to be avail / measureLevel(0), i.e. the tallest card in the
     WHOLE fleet, which meant the first card could not be revealed until
     every other product section had hydrated and been measured and the
     fleet had agreed with itself twice. Measured on staging Sep 5 2026 with
     realistic timing (900ms on the street, 700ms in the den, then click):
     the model landed at ~497ms and the card at ~1524ms - the instant
     `hound:fleet-complete` fired at ~1497ms. A second of a nameless model
     standing on the street, bought for one number.

     That number is not really a measurement. It is "the tallest card at the
     design width", a property of the CATALOGUE - it changes when copy or
     products change, never when the screen does. The proof is in the
     fleet's own readings, taken at both ends of the shipped range:

       1728x1117   795 801 828 904 915 995 998 1021   avail 928
       3840x2000   795 801 828 904 915 995 998 1021   avail 928

     Identical to the pixel, because the stage law pins every item's layout
     width and only the render scale s changes with the viewport. avail is
     the same at both for the same reason. So kd = avail / 1021 = 0.90891 at
     both, which is exactly the k/s this file computed before this change -
     the token reproduces today's number, it does not choose a new one.

     Tallest at the time of writing: 'Argentis Heather' Reflective Hoodie at
     1021 (Opium Black Box Tee 998, Argentis Grey Box Tee 995).

     A card that outgrows the token is NOT a reason to re-scale the fleet.
     It does what an over-tall card has always done: the per-card elision
     ladder climbs for that card alone, and if even that is not enough its
     own height lock caps at avail / kd and the surplus scrolls inside the
     signalled reading band (`data-rpg-fit="clipped"`). Type stays the same
     size across products either way, which is the whole reason a fleet
     maximum existed.

     WHEN THE CATALOGUE CHANGES, RE-READ IT: the sweep still measures the
     fleet, and `HoundShopFit.fleetMax()` reports what it found, so a drift
     between this constant and the shop is one console call away. */
  var REF_CARD_NAT = 1021;

  var CEIL = 0.85;  /* the DESIGN scale: what the card was drawn at. Only used
                       as the restore value for a card that has never been
                       fitted, so it is never seen for more than a frame. */
  /* The design ceiling is 1: a card may fill its own design column and no
     more. GROWTH IS THE STAGE'S JOB now, so the old GROW=1.25 cap and the
     kW gap measurement that fed it are gone - kW was structurally pinned at
     1 anyway (its input, min(gapL, gapR), is 0 on this grid because the
     gallery fills track 1 with gap 0), which made GROW unreachable dead
     code and left the card unable to grow at all. */
  var CEILING = 1;
  var FLOOR = 0.66; /* legibility bound. Was 0.72 from the pre-bump type
                       scale; after the painted-type pass the smallest CSS
                       size in the card is the 17px lore, which still paints
                       11.2px here. Deeper shrink is what keeps the FEATURES
                       list visible instead of folded (Evan, Aug 23). */
  var GUTTER = 10;
  /* The gallery includes the 360 badge below the visible garment, and its
     column begins lower than the card's top edge. Sharing the card's entire
     height budget therefore let the shoes/badge run 30-49px past the fold on
     short laptops. Reserve that measured offset for the turntable only. */
  var FIGURE_FOOT_CLEARANCE = 96;

  /* Elision ladder, applied only when even FLOOR cannot fit the tallest card.
     Level 1 folds the PASSIVE stat (decorative) and clamps the lore; level 2
     also folds the features list. Features used to fold first, which read as
     information going missing while a decoration stayed. */
  /* One elision level only. Level 2 used to fold the FEATURES list - but
     features are CONTENT, the product's selling points, and folding them
     read as information going missing (Evan, Aug 23: "we could fit this
     before... collapsing everything looks weird"). Decoration elides
     (passive stat, diagrams, icon strips, explainer notes); content shrinks
     to the floor and then SCROLLS behind the signalled band, never hides. */
  var LEVELS = ['', '1'];

  var cache = { sig: null, maxNat: [0, 0, 0], measured: [false, false, false] };
  var measuringNow = false;
  /* While a card's height/scale transition is in flight, its children resize
     EVERY FRAME - and each ResizeObserver delivery invalidated the cache and
     ran a full measuring excursion (~6 forced page layouts) mid-animation.
     The animation was fighting a measurement storm, which is what "laggy
     shrink" looks like. Writes that change geometry raise this deadline and
     the observer stays quiet until the transition is over. */
  var animGuardUntil = 0;
  var visibleRecheckTimers = [];
  /* True once two consecutive fleet readings have agreed - i.e. the fleet is
     whole and its max is final. Cleared whenever convergence is re-armed. */
  var fleetSettled = false;
  var selectorRecheckTimers = [];
  var selectorRecheckRaf = 0;

  function groupsAll() {
    return Array.prototype.slice.call(document.querySelectorAll('.product-modal-group'));
  }

  function isShowing(el) {
    var r = el.getBoundingClientRect();
    return r.height > 0 && r.width > 0;
  }

  /* Showing means it has a box. ON STAGE means the shopper can see it: the
     group is opaque and so is the section it lives in. The two differ during
     every den entrance and every staged handoff, which is precisely when the
     cold-open test above is asked. */
  function onStage(group) {
    if (!isShowing(group)) return false;
    var gs = getComputedStyle(group);
    if (gs.display === 'none' || gs.visibility === 'hidden' ||
        parseFloat(gs.opacity) < 0.05) return false;
    var sec = group.closest('[id^="shopify-section-"]');
    if (!sec) return true;
    var ss = getComputedStyle(sec);
    return ss.display !== 'none' && ss.visibility !== 'hidden' &&
      parseFloat(ss.opacity) >= 0.05;
  }

  /* FIRST-OPEN COMMIT GATE.
     A product selector makes its section display:block synchronously. The card
     fitter runs in the requestAnimationFrame immediately after that click, but
     a busy mobile browser can composite the newly displayed section before
     the fitted variables have been committed. That exposes the card at its
     fallback scale/position for one frame, then snaps it into place.

     Hide only the card that is ABOUT to be opened, while retaining its layout
     box so measurement stays honest. applyAll() clears the gate in the same
     frame that writes the final fit. This listener is capture-phase so it runs
     before product-selector-buttons reveals the section; programmatic deep-link
     clicks pass through the same path. The fail-open timer prevents a script or
     media edge case from leaving the purchase UI unavailable. */
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var button = event.target.closest('.product-selector-button[data-product-id]');
    if (!button) return;
    var productId = button.getAttribute('data-product-id');
    if (!productId) return;
    groupsAll().forEach(function (group) {
      var info = group.querySelector('[data-product-id]');
      if (!info || info.getAttribute('data-product-id') !== productId) return;
      var section = group.closest('[id^="shopify-section-"]');
      group.setAttribute('data-rpg-awaiting-fit', '');
      if (section) section.setAttribute('data-rpg-awaiting-product', '');
      syncInert(group);
      document.documentElement.classList.add('shop-product-pending');
      document.documentElement.setAttribute('data-shop-product-pending', productId);
      clearTimeout(group.__rpgFirstOpenFallback);
      group.__rpgFirstOpenFallback = setTimeout(function () {
        group.removeAttribute('data-rpg-awaiting-fit');
        syncInert(group);
      }, 500);
      clearTimeout(group.__rpgCompositionFallback);
      group.__rpgCompositionFallback = setTimeout(function () {
        if (section) section.removeAttribute('data-rpg-awaiting-product');
        syncInert(group);
        if (document.documentElement.getAttribute('data-shop-product-pending') === productId) {
          document.documentElement.classList.remove('shop-product-pending');
          document.documentElement.removeAttribute('data-shop-product-pending');
        }
      }, 4500);
    });
  }, true);

  /* THE GATE OWNS THE INERTNESS, AND NOTHING MIRRORS IT.
     ---------------------------------------------------------------------
     The gate hides the card with opacity, which is not inherited and so does
     not drag 17 `transition: all` descendants through a visibility
     transition on every product switch. `inert` carries the rest of what
     visibility:hidden was doing - out of the tab order, out of the
     accessibility tree - because it is a property, so it inherits nothing
     and animates nothing.

     THE FIRST VERSION OF THIS MIRRORED THE GATE FROM THE CALL SITES AND
     BROKE THE BUY BUTTON. `data-rpg-awaiting-product` is cleared by the
     SELECTOR's own code, not by this file; on the one path where the
     selector clears both markers without the fitter re-committing (clicking
     the armoury tile for the product already open, where nothing needs
     re-fitting) the mirror was never re-run, so a card that looked
     completely normal at opacity 1 stayed inert: Add to Cart did not fire
     and the tabs did not change until the shopper switched product.

     A mirror of a state you do not own is always one missed call site away
     from that. This DERIVES the property from the markers instead - the
     observer fires on any change to either one, by anyone, and there is no
     call site left to forget. The attributeFilter deliberately excludes
     `inert` itself - and lists only the two markers, so the pre-`inert`
     fallback's class write cannot re-enter it either. */
  /* `inert` is Safari 15.5+ / Chrome 102+ / Firefox 112+. On anything older
     the property assignment is silently inherited-by-nobody and does
     nothing, which would leave a gated card at opacity 0 but still
     keyboard-focusable and still announced - exactly the two things
     visibility:hidden used to buy and the only reasons it was replaced.
     Feature-detect, and where the property does not exist fall back to the
     old rule for that browser only: it pays the transition-storm cost this
     change was made to avoid, which is the correct trade against shipping a
     focus trap. */
  var INERT_OK = typeof HTMLElement !== 'undefined' && 'inert' in HTMLElement.prototype;

  function syncInert(group) {
    var sec = group.closest('[id^="shopify-section-"]');
    var gated = group.hasAttribute('data-rpg-awaiting-fit') ||
      !!(sec && sec.hasAttribute('data-rpg-awaiting-product'));
    if (INERT_OK) {
      if (group.inert !== gated) group.inert = gated;
      return;
    }
    if (group.classList.contains('rpg-inert-fallback') !== gated) {
      group.classList.toggle('rpg-inert-fallback', gated);
    }
  }

  function syncInertAll() { groupsAll().forEach(syncInert); }

  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(syncInertAll).observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-rpg-awaiting-fit', 'data-rpg-awaiting-product']
    });
  }

  function commitFirstOpen(group) {
    if (group.hasAttribute('data-rpg-awaiting-fit')) {
      /* THE FIRST COMMIT IS THE FINAL ONE, SO THE REVEAL IS IMMEDIATE.
         This used to hold the card until the whole fleet had hydrated and
         agreed with itself, because the scale was a fleet maximum and an
         interim maximum meant an interim scale - the 5% shrink. The scale is
         now avail / REF_CARD_NAT times s, and both terms are known at first
         paint, so there is no interim reading left to protect against and
         the wait was pure latency (~1.0s of nameless model, measured).

         What is still waited on is this card's OWN fit, which is one card
         and synchronous: applyAll() has committed --rpg-card-k/-shed/-nat
         for this group in the same frame, immediately above. The fail-open
         timer set at click time stays as the belt.

         A SWITCH STILL WAITS, and the reason is not the scale any more - it
         is the handoff. On a switch there is an OUTGOING model held on stage
         while this card is staged and fitted behind it, and cutting the
         incoming card in during that frame cost the swap its whole point:
         measured on staging at 1728x1117, den-play's click-to-playing went
         from 50-75ms across four control runs to 307ms, 396ms, 5071ms and one
         switch that never played, with `[HD-PROMO] play-rejected AbortError`
         on the promotion. Evan's brief is explicit that a slower model is a
         regression, so a switch keeps the gate it had and only a COLD open -
         nothing on stage at all, which is exactly the recording - skips it.

         ASK THE PAGE, NOT A FLAG. This was first written as a flag set by
         this file's own click listener, which never runs: the pre-ready
         intent bridge in layout/theme.liquid is registered at parse time on
         the same document capture phase and calls stopImmediatePropagation,
         so every listener this file adds for a product click is dead (see the
         report - the 500ms `__rpgFirstOpenFallback` belt is never armed
         either). "Is another card on stage" is answerable here and now. */
      var handoff = groupsAll().some(function (other) {
        return other !== group && onStage(other);
      });
      (window.HDLOG || function () {})('[HD-FIT] reveal',
        'refNat', REF_CARD_NAT, 'handoff', handoff,
        'fleetSettled', fleetSettled, 'timers', convergeTimers.length,
        'at', Math.round(performance.now()));
      if (handoff && !fleetSettled && convergeTimers.length) {
        syncInert(group);
        return;
      }
      clearTimeout(group.__rpgFirstOpenFallback);
      group.removeAttribute('data-rpg-awaiting-fit');
    }
    /* Belt for the observer's braces: the observer is async (microtask), and
       this runs in the same frame the card is revealed. */
    syncInert(group);
  }

  /* Stacked cards change height substantially between Details, Sizing and
     Durability. Because the whole shop is one long mobile scene, that reflow
     used to move the tab row after the tap and make the page feel as though it
     jumped. Capture the row's viewport position before the inline tab handler
     swaps panes, then restore that position on the next frame. */
  document.addEventListener('click', function (event) {
    if (window.innerWidth >= 1280) return;
    var tab = event.target.closest && event.target.closest('.product-info-tab-button');
    if (!tab) return;
    var row = tab.closest('.product-info-tabs');
    if (!row) return;
    var before = row.getBoundingClientRect().top;
    requestAnimationFrame(function () {
      var delta = row.getBoundingClientRect().top - before;
      if (Math.abs(delta) > 1) window.scrollBy(0, delta);
    });
  }, true);

  function viewportSig() {
    return window.innerWidth + 'x' + window.innerHeight;
  }

  /* s, and the one place it is decided.
     Floored at the legibility bound: a stage that shrinks below FLOOR would
     paint type under 8px, and it would also make the design column narrower
     than the floored card that has to sit in it. Above 1280px of width the
     floored stage (1140.48 * 0.66 = 752px) always fits the panel's 66%, so
     the floor costs nothing except on genuinely squat desktop windows, which
     is exactly where it is wanted. */
  function stageScale() {
    if (window.innerWidth < 1280) return 1;
    return Math.max(FLOOR, Math.min(window.innerWidth / REF_W, window.innerHeight / REF_H));
  }

  function writeStage(s) {
    var v = String(s);
    if (document.documentElement.style.getPropertyValue('--rpg-stage-s') !== v) {
      document.documentElement.style.setProperty('--rpg-stage-s', v);
    }
  }

  /* The one honest height input: the panel's own box minus its padding token.
     Everything nearer the card is content-sized and answers circularly
     (non-monotonic k is the signature of that mistake). */
  function panelAvail(s) {
    var groups = groupsAll();
    for (var i = 0; i < groups.length; i++) {
      if (!isShowing(groups[i])) continue;
      var panel = groups[i].closest('.width-sevenzero') || groups[i].closest('[id^="shopify-section-"]');
      if (panel) {
        var ps = getComputedStyle(panel);
        /* Keep a visible stage below the complete card (tabs included).
           The former min(paddingBottom, 24*s) could reserve ZERO when a
           section had no bottom padding. Use 72 design px, at least 48 real
           px on short desktops, and honour any larger authored inset. */
        var bottomClearance = Math.max(48, 72 * s, parseFloat(ps.paddingBottom) || 0);
        var chrome = (parseFloat(ps.paddingTop) || 0) + bottomClearance;
        var panelTop = Math.max(0, panel.getBoundingClientRect().top);
        var visibleHeight = Math.min(panel.clientHeight, window.innerHeight - panelTop);
        /* BOTH TERMS IN DESIGN PIXELS.

           The real room is divided by s because the card lives on a stage
           scaled by s: room/s is what that room is worth in the coordinate
           space the card's content is laid out in. `chrome` is a real
           measurement too, so the cap needs chrome/s and not chrome -
           subtracting a real 232px of panel padding from a design 1117px
           budget mixes the two spaces and quietly shrinks the card on
           exactly the screens whose chrome grows with the stage (measured:
           2592x1676 read avail 875 instead of 928, a card 6% too small,
           entirely from that unit error).

           Capped at REF_H's budget because a bigger screen buys a bigger
           STAGE, never a differently-proportioned card. Without the cap a
           viewport whose s is bound by WIDTH (1920x2100: s = 1.111 from the
           width, but 1.88 of height available) hands the card 1719 design
           pixels, it grows to fill them, and what is on screen is no longer
           the reference composition - it is a taller card in the same
           frame. The surplus is meant to be empty stage. */
        return Math.min((visibleHeight - chrome) / s, REF_H - chrome / s) - GUTTER;
      }
    }
    return window.innerHeight / s - GUTTER;
  }

  /* Measure every card at a given elision level (details pane active).
     Hidden product sections are revealed for the duration; the whole
     excursion is synchronous, so the browser never paints the reveal.
     Cost is ~2 forced layouts and it runs once per viewport per level. */
  function measureLevel(levelIdx) {
    var all = groupsAll();
    var groups = all.filter(function (g) {
      return !cache.measured[levelIdx] || g.__rpgDirty ||
        !(g.__rpgPaneNats && g.__rpgPaneNats[levelIdx]);
    });
    if (!groups.length) return cache.maxNat[levelIdx];
    measuringNow = true;
    var secs = groups.map(function (g) { return g.closest('[id^="shopify-section-"]'); });
    var restoreDisplay = secs.map(function (s) { return s ? s.style.display : ''; });
    var actives = groups.map(function (g) { return g.querySelector('.product-info-content.active'); });
    var restoreCompact = groups.map(function (g) { return g.getAttribute('data-rpg-compact'); });
    var restoreK = groups.map(function (g) { return g.style.getPropertyValue('--rpg-card-k'); });
    var restoreShed = groups.map(function (g) { return g.style.getPropertyValue('--rpg-card-shed'); });
    var restoreNat = groups.map(function (g) { return g.style.getPropertyValue('--rpg-card-nat'); });

    secs.forEach(function (s) { if (s && s.style.display === 'none') s.style.display = 'block'; });
    groups.forEach(function (g) {
      g.setAttribute('data-rpg-measuring', '');
      g.removeAttribute('data-rpg-fit');
      if (levelIdx > 0) g.setAttribute('data-rpg-compact', LEVELS[levelIdx]);
      else g.removeAttribute('data-rpg-compact');
      g.style.setProperty('--rpg-card-k', '1');
      g.style.setProperty('--rpg-card-shed', '0px');
      g.style.removeProperty('--rpg-card-nat');
    });

    /* Galleries in the same excursion, BATCHED: the first version measured
       them one at a time - a write-read-write per section is a forced layout
       per section, and it is most of where the 1.1s went. All writes, one
       layout, all reads. */
    var gals = [], galPrev = [];
    if (levelIdx === 0) {
      groups.forEach(function (g) {
        var sec2 = g.closest('[id^="shopify-section-"]');
        var gal = sec2 && sec2.querySelector('.product__gallery-container');
        if (!gal) return;
        gals.push(gal);
        galPrev.push([gal.style.getPropertyValue('--rpg-fig-k'), gal.style.getPropertyValue('--rpg-fig-shed')]);
        gal.setAttribute('data-rpg-measuring', '');
        gal.style.setProperty('--rpg-fig-k', '1');
        gal.style.setProperty('--rpg-fig-shed', '0px');
      });
    }

    var paneCount = 0;
    groups.forEach(function (g) {
      paneCount = Math.max(paneCount, g.querySelectorAll('.product-info-content').length);
    });
    var paneNats = groups.map(function () { return []; });
    for (var pi = 0; pi < Math.max(paneCount, 1); pi++) {
      groups.forEach(function (g) {
        var panes = g.querySelectorAll('.product-info-content');
        if (panes.length > pi) {
          Array.prototype.forEach.call(panes, function (q, j) { q.classList.toggle('active', j === pi); });
        }
      });
      void document.body.offsetHeight;
      groups.forEach(function (g, gi) { paneNats[gi][pi] = g.offsetHeight; });
      if (pi === 0) {
        gals.forEach(function (gal) { gal.__rpgFigNat = gal.offsetHeight; });
        /* The gap measurement that used to live here - four
           getBoundingClientRect reads per group, inside the excursion - fed
           the old width ceiling. Under the stage law the card's layout box IS
           its design column and its outer box is shed to equal what it
           paints, so the free space is known by construction and the gaps
           never have to be measured again. natW is kept: the elision
           machinery and the restore path both read it. */
        groups.forEach(function (g) { g.__rpgNatW = g.offsetWidth; });
      }
    }

    /* restore active panes, compact attrs, scale vars, section visibility */
    groups.forEach(function (g, gi) {
      var panes = g.querySelectorAll('.product-info-content');
      Array.prototype.forEach.call(panes, function (q) { q.classList.toggle('active', q === actives[gi]); });
      if (restoreCompact[gi] === null) g.removeAttribute('data-rpg-compact');
      else g.setAttribute('data-rpg-compact', restoreCompact[gi]);
      /* A first-run group has no prior values to restore. Leave it at the
         CEIL scale, never at the measuring k=1. */
      g.style.setProperty('--rpg-card-k', restoreK[gi] || String(CEIL));
      g.style.setProperty('--rpg-card-shed', restoreShed[gi] || '0px');
      if (restoreNat[gi]) g.style.setProperty('--rpg-card-nat', restoreNat[gi]);
      var store = g.__rpgPaneNats || (g.__rpgPaneNats = {});
      store[levelIdx] = paneNats[gi];
      g['__rpgNat' + levelIdx] = paneNats[gi][0] || 0;
    });
    gals.forEach(function (gal, gi) {
      gal.style.setProperty('--rpg-fig-k', galPrev[gi][0] || '1');
      gal.style.setProperty('--rpg-fig-shed', galPrev[gi][1] || '0px');
    });
    secs.forEach(function (s, si) { if (s) s.style.display = restoreDisplay[si]; });
    /* THE MUTE MUST OUTLIVE THE RESTORE BY ONE RECALC. Transitions do not
       need a paint to trigger, only two style recalcs: unmuting in the same
       batch that restores k pairs a muted k=1 against an unmuted restored k,
       and the card visibly animates FROM full size. Commit the restored
       values while still muted, then unmute. */
    void document.body.offsetHeight;
    groups.forEach(function (g) { g.removeAttribute('data-rpg-measuring'); });
    gals.forEach(function (gal) { gal.removeAttribute('data-rpg-measuring'); });
    void document.body.offsetHeight;

    /* Fleet max over EVERY group's stored value, not just the re-measured
       subset. */
    var maxNat = 0;
    all.forEach(function (g) {
      var nats = g.__rpgPaneNats && g.__rpgPaneNats[levelIdx];
      if (nats && nats[0]) maxNat = Math.max(maxNat, nats[0]);
    });
    cache.maxNat[levelIdx] = maxNat;
    cache.measured[levelIdx] = maxNat > 0; /* a zero measure means the shop had no box yet; retry later */
    /* ResizeObserver callbacks are ASYNC: a synchronous flag is already false
       by delivery time, so the excursion's own pane-flips re-invalidated the
       cache and spawned an excursion every frame - a self-sustaining loop
       that restarted the scale transition from 1 each frame and pinned the
       painted card at full size (matrix(1) measured live at 1434x767). Clear
       the flag two frames later, after the observer has delivered our noise. */
    if (measureLevel.__clearT) cancelAnimationFrame(measureLevel.__clearT);
    measureLevel.__clearT = requestAnimationFrame(function () {
      measureLevel.__clearT = requestAnimationFrame(function () { measuringNow = false; });
    });
    return maxNat;
  }

  function invalidate() {
    /* A FLEET THAT HAS BEEN INVALIDATED IS BY DEFINITION NOT SETTLED.
       This was one-way: two agreeing readings taken before a late arrival set
       it true, and nothing put it back, so the reveal gate below was already
       open by the time the numbers moved. Whatever invalidates the cache
       invalidates the agreement that was built on it. */
    fleetSettled = false;
    cache.sig = null;
    cache.measured = [false, false, false];
    groupsAll().forEach(function (g) { g.__rpgDirty = true; });
  }

  /* Content changes are LOCAL: a video getting its metadata, a variant swap,
     a metafield render - they change ONE product's height. Re-measuring the
     whole fleet for each of them is what turned a tab click into a 1.1s
     synchronous layout stall (profiled live: 1136ms of get offsetHeight).
     Only the source section goes dirty; the full sweep is reserved for
     viewport changes and fonts, which genuinely touch everything. */
  function invalidateFor(el) {
    var sec = el && el.closest ? el.closest('[id^="shopify-section-"]') : null;
    var g = sec && sec.querySelector('.product-modal-group');
    if (!g) { invalidate(); return; }
    g.__rpgDirty = true;
  }

  function applyAll() {
    /* THE STAGE SCALE IS THE ARMOURY'S TOO, AND THE ARMOURY IS ON STAGE
       BEFORE ANY CARD IS (Evan, Sep 18).
       -----------------------------------------------------------------
       This used to be published further down, AFTER the `!groups.length`
       bail, so it only ever ran once a product section had hydrated a
       `.product-modal-group` - i.e. on the first product click. But the
       armoury panel is scaled by the same token in hound-shop-card.css
       (`transform: scale(var(--rpg-stage-s, 1))`), and that declaration is
       unconditional. With the token still unset it fell back to 1, so the
       Armory painted at its full 468px design width from den entry and then
       SNAPPED to 468 * s the instant the first fit committed: measured at
       1367x808, width 468 -> 339, x +45, y +79, CLS 0.028.

       Publishing it here costs one cheap arithmetic write per fit pass and
       means the armoury is scaled correctly on its first paint, before
       anything is clicked. writeStage is a no-op when the value has not
       changed, and the later write in this same function is left alone. */
    writeStage(stageScale());

    var groups = groupsAll();
    if (!groups.length) return;

    /* STACKED LAYOUTS DO NOT GET SCALED: below 1280 the card sits under the
       model and the column scrolls; shrinking only costs width. */
    if (window.innerWidth < 1280) {
      writeStage(1);
      groups.forEach(function (g) {
        g.removeAttribute('data-rpg-measuring');
        g.removeAttribute('data-rpg-compact');
        g.setAttribute('data-rpg-fit', 'fits');
        g.style.setProperty('--rpg-card-k', '1');
        g.style.setProperty('--rpg-card-shed', '0px');
        g.style.removeProperty('--rpg-card-nat');
        var sec2 = g.closest('[id^="shopify-section-"]');
        var gal = sec2 && sec2.querySelector('.product__gallery-container');
        if (gal) { gal.style.removeProperty('--rpg-fig-k'); gal.style.removeProperty('--rpg-fig-shed'); }
        commitFirstOpen(g);
      });
      return;
    }

    /* Nothing on screen = nothing to measure honestly; wait for a box. */
    var anyShowing = groups.some(isShowing);
    if (!anyShowing) return;

    /* A card can become visible without a selector click - the ?den= deep
       link most of all - and its content hydrates AFTER first measure (the
       notify form collapses, metafield tables render). Whenever the visible
       card changes identity, it gets the same delayed re-checks a click
       gets; without this a deep-linked card kept its stale height and its
       buy rows escaped the frame (1920x1030, Aug 23). */
    var visNow = groups.filter(isShowing)[0] || null;
    if (visNow && visNow !== applyAll.__lastVis) {
      visibleRecheckTimers.forEach(clearTimeout);
      visibleRecheckTimers = [];
      applyAll.__lastVis = visNow;
      /* A newly revealed card SNAPS to its first fit - watching it ease into
         place read as wobble. Transitions come back once it has landed. */
      visNow.classList.add('rpg-settling');
      setTimeout(function () { visNow.classList.remove('rpg-settling'); }, 380);
      var redo = function () {
        if (applyAll.__lastVis !== visNow || !isShowing(visNow)) return;
        visNow.__rpgDirty = true;
        schedule();
      };
      visibleRecheckTimers.push(setTimeout(redo, 900));
      visibleRecheckTimers.push(setTimeout(redo, 2400));
    }

    if (cache.sig !== viewportSig()) { invalidate(); cache.sig = viewportSig(); }

    /* The stage scale must be committed BEFORE anything is measured: it sets
       the grid's real width, and the design widths of the card and the
       gallery hang off that grid. Measuring first would measure the previous
       screen's stage. */
    var s = stageScale();
    writeStage(s);

    var avail = panelAvail(s);
    if (avail <= 0) return;

    /* Still measured, and for two things that are genuinely per-card: each
       group's own natural height (the height lock below) and the
       "the shop has a box yet" guard. It no longer decides the scale, so
       nothing in the first open waits for the whole fleet to arrive. */
    var fleetNat = measureLevel(0);
    if (!fleetNat) return;
    var maxNat = REF_CARD_NAT;

    var level = 0;
    /* kd is the DESIGN scale: what fraction of its own design column this
       card's content needs. Legibility, though, is a property of PAINTED
       pixels, so the elision ladder and the floor below are both judged on
       kd * s and never on kd alone - a 4K screen has no legibility problem
       at kd = 0.7 and a 1280x720 one has a severe problem at the same
       number. */
    var kd = avail / REF_CARD_NAT;
    /* Elide, never shrink past FLOOR and never overflow silently. Each level
       is adopted only if it actually bought height (a level that measures no
       smaller is recorded and never re-tried: re-trying a rejected compact on
       every settle tick is the attribute thrash that corrupted 1366x620). */
    while (kd * s < FLOOR && level < LEVELS.length - 1) {
      var next = measureLevel(level + 1);
      if (!next || next >= cache.maxNat[level]) break;
      level += 1;
      kd = avail / next;
    }
    /* Levels above 0 stay measurement-driven: the ladder is only entered
       when the token-derived kd cannot clear the legibility floor, which no
       shipped shape does (0.909 at 1728x1117, 1.63 at 3840x2000 against a
       0.66 floor), so this never runs on a first open. It is still one
       synchronous excursion when it does - never a wait for convergence. */
    if (level > 0) maxNat = cache.maxNat[level];

    if (kd > CEILING) kd = CEILING;
    /* k is the RENDER scale, and it is the number every scale variable in
       this file now carries: design scale times stage scale. When the floor
       has to bite, it bites here, on the painted size, and kd is recovered
       from it so the height lock below still caps the frame in design
       pixels. Because s itself is floored at FLOOR, kd can never come back
       out of this above 1, so a floored card still cannot paint wider than
       its own design column. */
    var k = kd * s;
    if (k < FLOOR) { k = FLOOR; kd = Math.min(1, k / s); }

    groups.forEach(function (g) { g.__rpgDirty = false; });

    /* THE CARD IS AS TALL AS WHAT IS IN IT.
       -------------------------------------------------------------------
       Evan, Sep 3 2026: "the product detail UI is showing a lot of extra for
       some products, it should adjust to the height of the content". That is
       a decision, and it overrides the fleet-wide height lock this block used
       to hold.

       What the lock was FOR is kept: `k` is still derived fleet-wide from
       `maxNat`, so every product renders at the SAME scale and type does not
       change size between items. It is the HEIGHT that follows content now.

       Measured at 1728x1117 before this change: avail 928, maxNat 998 set by
       'Opium Black' Box Tee, so every card was pinned at 998 design px -
       'Thorn Heart' needs 712 and carried 286px of blank stone, Onyx SS needs
       832 and carried 166. The blank landed inside .product__text because it
       is the only `flex: 1 1 auto` child of .product__info.

       Per-ACTIVE-pane, not per-card-tallest: a constant tallest-pane lock was
       tried in August and rejected by Evan for the same reason in miniature
       (a slab of empty stone under a short Durability tab), which is why the
       CSS carries a 0.3s height transition - the frame EASES to the new
       height rather than snapping, on a tab switch and on a product switch
       alike. `k` never changes with it, so nothing rescales.

       The per-card 8px hysteresis below is what absorbs hydration noise; it
       is the only thing that used to be fleet-wide here. */
    /* Apply to EVERY card, hidden ones included, so opening another product
       arrives pre-fitted instead of re-fitting in view. */
    var nextK = String(k);
    groups.forEach(function (g) {
      var own0 = g.__rpgNat0 || maxNat;
      /* Every comparison against `avail` below is in DESIGN pixels, so it
         takes kd, not the render scale k. */
      var useCompact = level > 0 && own0 * kd > avail + 1;
      var eff = useCompact ? (g['__rpgNat' + level] || own0) : own0;
      /* The frame follows the ACTIVE pane's height - a short pane shrinks the
         card, a tall one grows it up to the band - and ELIDE-FIRST, per card,
         per PANE (Evan, Aug 23): if the active pane cannot fit at this card's
         level, climb the elision ladder for THIS card before falling back to
         an internally-scrolling band. Levels are measured lazily and cached
         fleet-wide, so the climb is one cheap lookup after the first time. */
      /* HYSTERESIS: the level starts from what the card ALREADY shows and
         only moves with margin. Recomputing from scratch on every settle
         re-measure made borderline cards oscillate - folds popping in and
         out while the card opened ("can't decide if it wants to shrink",
         Evan, Aug 23). Climbing still happens the moment the pane truly
         does not fit; descending needs 24px of clear room. */
      var attrLevel = LEVELS.indexOf(g.getAttribute('data-rpg-compact') || '');
      if (attrLevel < 0) attrLevel = 0;
      var gLevel = Math.max(useCompact ? level : 0, attrLevel);
      var activeIdx = 0;
      var panes = g.querySelectorAll('.product-info-content');
      for (var ai = 0; ai < panes.length; ai++) {
        if (panes[ai].classList.contains('active')) { activeIdx = ai; break; }
      }
      var natAt = function (L) {
        var arr = g.__rpgPaneNats && g.__rpgPaneNats[L];
        return (arr && arr[activeIdx]) || 0;
      };
      var floorLevel = useCompact ? level : 0;
      while (gLevel > floorLevel) {
        var lower = natAt(gLevel - 1);
        if (lower && lower * kd < avail - 24) gLevel -= 1; else break;
      }
      var effA = natAt(gLevel) || eff;
      while (effA * kd > avail + 1 && gLevel < LEVELS.length - 1) {
        measureLevel(gLevel + 1);
        var nx = natAt(gLevel + 1);
        if (!nx || nx >= effA) break;
        gLevel += 1;
        effA = nx;
      }
      eff = effA;
      if (gLevel > 0) {
        if (g.getAttribute('data-rpg-compact') !== LEVELS[gLevel]) g.setAttribute('data-rpg-compact', LEVELS[gLevel]);
      } else if (g.hasAttribute('data-rpg-compact')) {
        g.removeAttribute('data-rpg-compact');
      }
      /* Shed is priced from the card's own TALLEST pane, so the reserve (and
         with it the card's centred position) is constant across tab switches.
         A shorter tab under-fills the reserve slightly; a moving card was the
         bug, a quiet gap is not. */
      /* The height LOCK is what makes tab switches invisible: the group's
         layout height is pinned to its tallest pane, so the stone frame, the
         centred position and the pinned buy row are all constant while tabs
         swap content inside it. Without it the reserve tracked the ACTIVE
         pane and the card re-centred on every switch (measured -181px top /
         +331px height at 1512x950). */
      /* The frame must never exceed the panel: when even full elision leaves
         eff*k past avail, the lock caps at avail/k and the surplus scrolls
         inside the reading band (which signals its overflow). An uncapped
         lock pushed the whole card past the fold at 1434x767. */
      var lockNat = Math.min(eff, avail / kd);
      /* Sub-8px corrections are measurement noise from hydration, and each
         one played a visible 0.3s settle. They are not worth animating. */
      var prevNat = parseFloat(g.style.getPropertyValue('--rpg-card-nat')) || 0;
      if (prevNat && prevNat <= avail / kd && Math.abs(prevNat - lockNat) < 8) lockNat = prevNat;
      var nextNat = lockNat + 'px';
      var nextShed2 = (lockNat * (k - 1)) + 'px';
      var moved = false;
      if (g.style.getPropertyValue('--rpg-card-k') !== nextK) { g.style.setProperty('--rpg-card-k', nextK); moved = true; }
      if (g.style.getPropertyValue('--rpg-card-shed') !== nextShed2) { g.style.setProperty('--rpg-card-shed', nextShed2); moved = true; }
      if (g.style.getPropertyValue('--rpg-card-nat') !== nextNat) { g.style.setProperty('--rpg-card-nat', nextNat); moved = true; }
      if (moved) animGuardUntil = performance.now() + 450;
      var nextFit = eff * kd > avail + 1 ? 'clipped' : 'fits';
      if (g.getAttribute('data-rpg-fit') !== nextFit) g.setAttribute('data-rpg-fit', nextFit);
      g.style.setProperty('--rpg-card-avail', Math.round(avail) + 'px');
      commitFirstOpen(g);
    });

    /* Fit the gallery into the same band the card honours. */
    groups.forEach(function (g) {
      var sec2 = g.closest('[id^="shopify-section-"]');
      var gal = sec2 && sec2.querySelector('.product__gallery-container');
      if (!gal || !gal.__rpgFigNat) return;
      /* Gallery growth was REVERTED in August because scaling the figure
         about its centre painted it past its grid track: at 3840x2000 it
         overlapped the armoury by 237px and the card by 233px, and the
         overlapping video container swallowed clicks on the armoury's
         rightmost product column - a checkout-flow breaker. That was a bare
         scale cap with no idea where the track ended. The gallery grows
         again now because the composition math it was missing exists: the
         figure's layout box is its DESIGN width, its track is that width
         times s, and the inline shed makes its outer box equal its painted
         box, so at fkd = 1 it fills its track exactly and can never reach
         its neighbour's.

         fkd stays min(1, ...) - shrink-only - because that clamp is about
         CONTENT (a figure taller than its band), which is a design-space
         question. The growth is s, applied below. */
      var figAvail = Math.max(1, avail - FIGURE_FOOT_CLEARANCE);
      var fkd = Math.min(1, figAvail / gal.__rpgFigNat);
      /* The turntable model deliberately paints larger than its layout box
         (individual scale + transform scale).  Budget that painted extent as
         well: otherwise intermediate-height desktops can "fit" the gallery
         while the model itself lands just below the fold. */
      var model = gal.querySelector('.product-video-container[data-model="true"]');
      var grid = gal.closest('.rpg-shop');
      if (model && grid && model.offsetHeight) {
        var modelStyle = getComputedStyle(model);
        var individualScale = parseFloat(modelStyle.scale);
        if (!isFinite(individualScale)) individualScale = 1;
        var transformScaleY = 1;
        if (modelStyle.transform && modelStyle.transform !== 'none') {
          try {
            var matrix = new DOMMatrixReadOnly(modelStyle.transform);
            transformScaleY = Math.hypot(matrix.c, matrix.d) || 1;
          } catch (_matrixError) {}
        }
        var paintedNatural = model.offsetHeight * individualScale * transformScaleY;
        var gridRect = grid.getBoundingClientRect();
        var headerBudget = parseFloat(getComputedStyle(document.documentElement)
          .getPropertyValue('--hound-header-h')) || 104;
        var centreY = gridRect.top + gridRect.height / 2;
        /* paintedNatural is an offsetHeight, so it is DESIGN pixels; the grid
           rect and the header are real pixels. Divide the real half-band by s
           to compare like with like, or the guard reads as a hard clamp on
           every screen bigger than the reference. */
        var safeHalf = Math.min(centreY - headerBudget, innerHeight - centreY) / s;
        if (paintedNatural > 0 && safeHalf > 0) {
          fkd = Math.min(fkd, (safeHalf * 2) / paintedNatural);
        }
      }
      /* --rpg-fig-k carries the FULL render scale, exactly as --rpg-card-k
         does, so the CSS that reads it needs to know about one number and
         not two. */
      var fk = fkd * s;
      var fkS = String(fk);
      var fShed = (gal.__rpgFigNat * (fk - 1)) / 2 + 'px';
      if (gal.style.getPropertyValue('--rpg-fig-k') !== fkS) gal.style.setProperty('--rpg-fig-k', fkS);
      if (gal.style.getPropertyValue('--rpg-fig-shed') !== fShed) gal.style.setProperty('--rpg-fig-shed', fShed);
    });

    /* The armoury (.product-selector-buttons) used to be driven from here
       by a --rpg-sel-g growth factor that was hardcoded to 1, which made
       both this loop and the CSS rule that read it dead code. It is now a
       stage item like the card and the gallery: a DESIGN width times s, in
       assets/hound-shop-card.css, reading the same --rpg-stage-s this file
       publishes. Nothing about it needs measuring, so nothing about it
       belongs in the fitter. */

    /* Overflow must be VISIBLE: fade + chevron whenever the hidden-scrollbar
       reading band genuinely holds more below its fold. */
    groups.forEach(function (g) {
      if (!isShowing(g)) return;
      var text = g.querySelector('.product__text');
      if (!text) return;
      var over = text.scrollHeight > text.clientHeight + 4;
      if (over !== g.hasAttribute('data-rpg-overflow')) {
        if (over) g.setAttribute('data-rpg-overflow', ''); else g.removeAttribute('data-rpg-overflow');
      }
    });
  }

  /* Compact-mode disclosure wiring. The PASSIVE stat folds at level 1 (it is
     decorative), the features list only at level 2, and both headings carry a
     caret affordance so a folded section reads as openable rather than
     missing. Inert outside compact mode. */
  function wireCompact(group) {
    if (group.__rpgCompactWired) return;
    group.__rpgCompactWired = true;

    /* Clip only while the frame is actually easing between pane heights. */
    ['transitionrun', 'transitionstart'].forEach(function (n) {
      group.addEventListener(n, function (e) {
        if (e.target === group && (e.propertyName === 'height' || e.propertyName === 'margin-bottom')) {
          group.classList.add('rpg-animating');
        }
      });
    });
    ['transitionend', 'transitioncancel'].forEach(function (n) {
      group.addEventListener(n, function (e) {
        if (e.target === group && (e.propertyName === 'height' || e.propertyName === 'margin-bottom')) {
          group.classList.remove('rpg-animating');
          /* The incoming pane waits for the frame: content that appears
             before the stone has grown reads as living outside its frame
             (Evan, Aug 23). Cleared here = revealed the moment the height
             lands; the fallback timer in the tab-click handler covers
             switches whose height does not change. */
          group.classList.remove('rpg-pane-incoming');
        }
      });
    });

    var lore = group.querySelector('.lore-section');
    if (lore) {
      lore.addEventListener('click', function () {
        if (group.hasAttribute('data-rpg-compact')) group.classList.toggle('rpg-lore-open');
      });
    }

    Array.prototype.forEach.call(group.querySelectorAll('.section-heading'), function (h) {
      var label = (h.textContent || '').trim();
      if (/^passive/i.test(label)) {
        h.setAttribute('data-rpg-fold', 'passive');
        h.setAttribute('data-rpg-count', 'view');
        h.addEventListener('click', function () {
          if (group.hasAttribute('data-rpg-compact')) group.classList.toggle('rpg-passive-open');
        });
      }
    });

    var text = group.querySelector('.product__text');
    if (text) {
      text.addEventListener('scroll', function () {
        var more = text.scrollHeight - text.scrollTop - text.clientHeight > 6;
        if (more) group.setAttribute('data-rpg-overflow', ''); else group.removeAttribute('data-rpg-overflow');
      }, { passive: true });
    }
  }

  /* THE PREROLL: measuring the fleet at the beat BEFORE it is needed.
     ---------------------------------------------------------------------
     applyAll() refuses to measure when nothing is on stage ("nothing on screen
     = nothing to measure honestly"), and until the director existed nothing
     ever was until the shopper clicked a product. Instrumented on staging: at
     the moment of the first click every group still read nat0 = 0 - the fitter
     had never run - so the FIRST fit was also the first measurement, taken on
     content that was still 50px short. Half a second later the fleet grew
     (948 -> 998 on the tallest card) and the card the shopper was already
     looking at re-fitted from k 0.9789 to 0.9298: a visible 5% shrink, on
     every first open, at every shape.

     No gate can fix that, because the interim number was on screen before any
     gate could be lifted. The fix is to have MEASURED ALREADY. The director
     calls this at the market beat - the shopper is looking at the street, the
     panel is hidden - and the fleet's own hydration is what the measurement
     provokes, so it happens two or three seconds before the click instead of
     during it.

     Nothing paints. Displaying a section and restoring it inside one
     synchronous task is the same trick measureLevel() already uses for hidden
     cards: the browser cannot paint in the middle of a task, so the reveal is
     never composited. The restore is in a `finally` because applyAll() has
     several honest early returns and a section left display:block would put a
     whole product card on the street. */
  var prerollAllowed = false;
  /* prerollAllowed and sweepAllowed are two different permissions and were one
     flag, which is why the fleet was never warmed on the path that needed it
     most. The PREROLL - forcing one section open so applyAll has a panel to
     measure - belongs to the market beat only, because once a product is on
     stage there is a real box. The SWEEP - laying the fleet out so nothing is
     cold - belongs to BOTH den beats: a `?den=` deep link commits a product
     within a frame of entering, which cancelled the sweep before it had laid
     out anything, and every later switch then paid first-open cost. That is
     the 400-5000ms tail den-play sees on the first visit to each product. */
  var sweepAllowed = false;
  var sweepSections = [], sweepRestore = [], sweepRunning = false;
  /* TWO SWEEPS MUST NEVER OVERLAP. cancelSweep() can only clear the flag; the
     rAF callback already queued still runs, and because the applier re-arms
     sweepAllowed in the same tick that callback used to resume as a SECOND
     loop beside the one hydrateFleet then started. Two loops share one list:
     one populates while the other restores, and the restore lands on the
     section a switch has just adopted - measured as the outgoing product's
     loading sprite reappearing 8ms into a switch. A generation makes a
     cancelled sweep stay cancelled. */
  var sweepGen = 0;

  /* A SYNCHRONOUS DISPLAY IS NOT A LAYOUT SHOPIFY WILL ANSWER.
     ---------------------------------------------------------------------
     Traced on staging at 3840x2000: all eight <shopify-accelerated-checkout>
     blocks had their children in the DOM 700ms BEFORE the first click, and the
     fleet max was still 948 at the click, reaching 998 only 160ms after it -
     once the clicked section was displayed for real. The children arrive while
     the section is display:none; the block acquires HEIGHT only when it is laid
     out in a painted frame. Every measurement this fitter takes is inside a
     synchronous excursion, which by design never paints, so it could ask the
     wallet its height forever and be told nothing.

     So the market beat lays the WHOLE fleet out - not one section, which is
     what the first version of this did and which is exactly why the failure
     followed which product the shopper clicked first - and holds it out across
     real frames until every wallet has a box. Then one convergence on a fleet
     where nothing is cold.

     Invisible and inert while it happens: the sections' own inline opacity is
     already 0, and both that and pointer-events are asserted here rather than
     assumed, because eight full-size product cards at opacity 0 would otherwise
     sit over the armoury and swallow its clicks. */
  /* ADDITIVE, AND CALLED EVERY FRAME OF THE SWEEP. Product sections are
     hydrated on demand and arrive over hundreds of milliseconds - instrumented
     at 3840x2000: 2 sections at t+0, 3 at t+491, 4 at t+516, 6 at t+664, all 8
     at t+1032. A one-shot lay-out covers whichever happened to exist when it
     ran, which is the same defect as laying out one section: the fleet max
     then depends on arrival order, and so does whether the reveal is final. */
  function layFleetOut() {
    groupsAll().forEach(function (g) {
      var sec = g.closest('[id^="shopify-section-"]');
      if (!sec || getComputedStyle(sec).display !== 'none') return;
      /* A SECTION LAID OUT FOR MEASUREMENT IS NOT LOADING ANYTHING. Its
         loading sprite is shown by default until a switch hides it, so simply
         giving the section a box put eight spinners into the document - a true
         statement about the page, and one den-play rightly reports.

         MARK THE SECTION; DO NOT TOUCH THE SPRITE. The first version added the
         selector's own `hidden` class to those indicators, which is writing
         into another owner's state - the exact thing this whole round is
         about - and it showed: the deep-linked product's sprite came back on
         at 7ms into the first switch, every time, because the selector and
         this file were both deciding one class. The marker below is purely
         this file's, one CSS rule reads it (assets/hound-shop-card.css), and
         releasing the section removes it whoever else has taken the section
         over in the meantime. */
      sec.setAttribute('data-rpg-sweeping', '');
      sweepSections.push(sec);
      sweepRestore.push([sec.style.display, sec.style.opacity, sec.style.pointerEvents]);
      sec.style.opacity = '0';
      sec.style.pointerEvents = 'none';
      sec.style.display = 'block';
    });
    return sweepSections.length > 0;
  }

  /* A CLICK CLAIMS ITS SECTION LONG BEFORE THAT SECTION IS OPAQUE.
     ---------------------------------------------------------------------
     putFleetAway's ownership test used to be opacity alone, and opacity is
     the LAST thing a commit writes. The selector stages its target at
     display:block/opacity:0 - which is byte for byte what a swept section
     looks like - hydrates and fits it, and only then raises it. In between
     it cues the product beat, and the beat handler below calls cancelSweep,
     so the sweep handed back `display: none` on the very section the shopper
     had just asked for. The commit then set opacity:1 on a display:none
     section: `data-product` correct, card never seen. Measured on the local
     dev server before this change, 4 of 5 first clicks; on staging, 3 of 10.

     The claim is legible from the moment of the click, because the selector
     writes it synchronously: `data-shop-product-pending` on <html> carries
     the product id a selection is steering toward, and is cleared when that
     selection commits or fails. Read it, and the sweep stops handing back
     sections it no longer owns. Deliberately NOT `data-product`: that names
     the product already equipped, which for a swept (hidden) section means
     the one a switch has just left, and skipping its restore would strand a
     display:block card on the street. */
  function sweepClaimedBy(sec) {
    var claim = document.documentElement.getAttribute('data-shop-product-pending');
    if (!claim) return false;
    var info = sec.querySelector('[data-product-id]');
    return !!info && info.getAttribute('data-product-id') === claim;
  }

  function putFleetAway() {
    sweepSections.forEach(function (sec, i) {
      /* NEVER PUT AWAY A SECTION SOMEBODY ELSE HAS TAKEN. The sweep holds its
         sections at opacity 0; a product committed while the sweep was running
         is at opacity 1, and restoring its display would delete the card the
         shopper just asked for. Ownership is legible, so check it. */
      /* THE MARKER AND THE INERTNESS GO BACK WHATEVER HAPPENED TO THE SECTION.
         Only display and opacity are the sweep's to hand back, and only while
         the sweep still owns them. pointer-events is different: the sweep is
         the only thing that ever writes it, so skipping its restore on an
         adopted section left `pointer-events: none` on the card a shopper had
         just opened - the shop was on screen and could not be touched, which
         the layout suite reported as `no shop` at 1512x950 and 1366x620. */
      sec.removeAttribute('data-rpg-sweeping');
      sec.style.pointerEvents = sweepRestore[i][2];
      if (sec.style.opacity !== '0' || sweepClaimedBy(sec)) return;
      sec.style.display = sweepRestore[i][0];
      sec.style.opacity = sweepRestore[i][1];
    });
    sweepSections = []; sweepRestore = [];
  }

  function cancelSweep() {
    sweepGen += 1;
    putFleetAway();
    sweepRunning = false;
  }

  /* Bounded by the CONDITION (every wallet has a box) and, only as a backstop
     against a wallet that never renders at all, by a frame count. */
  var SWEEP_MAX_FRAMES = 150;
  /* Frames a section must be laid out before its wallet has had its chance. */
  var LAYOUT_FRAMES = 3;
  /* Frames the fleet count must hold steady before the sweep calls it whole. */
  var COUNT_STABLE_FRAMES = 8;

  /* Has any section still never been laid out in a real frame? Once they all
     have, the sweep has nothing left to do and must not run again: the beat is
     re-applied on EVERY product commit, and re-sweeping there put eight
     sections through a lay-out on every switch - measurable as a loading
     sprite on five switches in six, and paid for by the switch the shopper is
     actually watching. This is a one-time burst per visit, and this is the
     line that makes it one. */
  function fleetNeedsLayout() {
    var pending = 0;
    groupsAll().forEach(function (g) {
      var sec = g.closest('[id^="shopify-section-"]');
      if (!sec) return;
      if (getComputedStyle(sec).display !== 'none') {
        /* ON STAGE IS THE MOST THOROUGHLY LAID-OUT A SECTION CAN BE. Without
           this stamp the committed product is the one section the sweep never
           covers, so the moment the shopper switches away it retires into the
           never-laid-out pool and the next commit starts a whole new sweep -
           one per switch, which is the loading sprite that came back. */
        sec.__rpgLaidOut = LAYOUT_FRAMES;
        return;
      }
      if ((sec.__rpgLaidOut || 0) < LAYOUT_FRAMES) pending += 1;
    });
    return pending > 0;
  }

  function hydrateFleet() {
    if (sweepRunning || !sweepAllowed || !fleetNeedsLayout()) return;
    sweepRunning = true;
    sweepGen += 1;
    var gen = sweepGen;
    var t0 = performance.now();
    var frames = 0, lastCount = -1, stable = 0;
    (function wait() {
      if (gen !== sweepGen) return;   /* superseded: the new sweep owns the list */
      frames += 1;
      /* A product intent ends the sweep immediately: the shopper's click owns
         the stage and this must never be juggling display underneath it. */
      if (!sweepAllowed) { putFleetAway(); sweepRunning = false; return; }
      /* THE FLEET MAY NOT HAVE ARRIVED YET. Product sections are hydrated on
         demand, so the beat can change before a single one of them exists -
         the first version of this returned silently in exactly that case and
         nothing ever came back for it. Wait for them the same way it waits for
         the wallets: on the condition, not on a guess about when. */
      layFleetOut();
      var n = sweepSections.length, cold = 0, unpainted = 0;
      for (var i = 0; i < n; i++) {
        sweepSections[i].__rpgLaidOut = (sweepSections[i].__rpgLaidOut || 0) + 1;
        if (sweepSections[i].__rpgLaidOut < LAYOUT_FRAMES) unpainted += 1;
        var w = sweepSections[i].querySelector('shopify-accelerated-checkout');
        if (w && w.offsetHeight < 1) cold += 1;
      }
      /* THE TERMINATOR IS LAYOUT, NOT THE WALLET.
         "Every wallet has a box" is unsatisfiable: measured on staging, three
         of the eight products (ThrnEr, xWmkh7, HGRSp) render a
         <shopify-accelerated-checkout> with its two children and a height of
         ZERO, every time, at both shapes - Shop Pay simply does not offer a
         wallet for them. Waiting on all eight would always run to the frame
         cap and would always be waiting for something that is never coming.

         What the fitter actually needs is that every section has been LAID OUT
         in real frames at least once, because that is the thing a synchronous
         excursion cannot do and the only thing the wallet was ever waiting for.
         A product whose wallet is going to render has rendered it by then; one
         whose wallet never renders is finished too, and is correctly measured
         without one.

         HOW MANY PRODUCTS THERE ARE is known from the start even though the
         sections are not: the armoury renders one selector button per product
         before any of them is hydrated. */
      /* AND THE FLEET HAS STOPPED GROWING. The selector button count is NOT
         the fleet size - measured: 12 buttons, 8 product sections, because the
         armoury grid keeps its blank slots and retired products keep their
         buttons. Waiting for 12 sections waits forever, which is what the
         first version of this terminator did: it always ran to the frame cap.

         So: every section known right now has been laid out, and no new one
         has appeared for a while. Arrival gaps measured 31-315ms, so the
         stability window sits above the largest - and it is a DEBOUNCE, not a
         deadline, because a section arriving after the sweep ends fires
         `hound:section-hydrated` and starts another one. Stopping early costs
         a second cheap sweep, never a stale fleet. */
      var count = groupsAll().length;
      if (count !== lastCount) { lastCount = count; stable = 0; } else stable += 1;
      var complete = n > 0 && unpainted === 0 && stable >= COUNT_STABLE_FRAMES;
      if (!complete && frames < SWEEP_MAX_FRAMES) { requestAnimationFrame(wait); return; }
      putFleetAway();
      sweepRunning = false;
      (window.HDLOG || function () {})('[HD-FIT] fleet-hydrated frames', frames,
        'ms', Math.round(performance.now() - t0), 'stillCold', cold,
        'sections', n);
      invalidate();
      fitAll();
      converge();
    })();
  }

  /* The converge runs that follow the sweep happen with nothing on stage, so
     they still need one section laid out to have an honest panel to measure
     against. By then every wallet has rendered once, and a rendered wallet
     keeps its height across a synchronous re-display, so this is measurement
     only - it is no longer where hydration is supposed to happen. */
  function applyAllPrerolled() {
    var groups = groupsAll();
    if (!groups.length || groups.some(isShowing) || !prerollAllowed) {
      applyAll();
      return;
    }
    var section = groups[0].closest('[id^="shopify-section-"]');
    if (!section) { applyAll(); return; }
    var restore = section.style.display;
    section.style.display = 'block';
    try {
      applyAll();
    } finally {
      section.style.display = restore;
    }
  }

  function fitAll() {
    groupsAll().forEach(wireCompact);
    applyAllPrerolled();
  }

  /* The shop is revealed by an animated panel transition; re-fit until the
     inputs stop changing, then stop. Bounded. */
  var settleTimer = null;

  function geomSig() {
    return groupsAll().map(function (g) {
      var r = g.getBoundingClientRect();
      return Math.round(r.top) + ':' + Math.round(r.height);
    }).join('|') + '@' + window.innerHeight;
  }

  function settle() {
    var last = null;
    var stable = 0;
    var ticks = 0;
    clearInterval(settleTimer);
    settleTimer = setInterval(function () {
      /* RE-FIT ONLY WHEN A RE-FIT COULD CHANGE SOMETHING.
         This loop used to call fitAll() on every one of its up-to-40 ticks -
         twenty full applyAll() passes a second, for up to two seconds, armed
         afresh by every schedule() and therefore by every tab and product
         click. But fitAll() is a pure function of (dirty cache, available
         band, fleet natural): if no group is dirty and the geometry has not
         moved since the previous tick, re-running it re-applies the values
         already on the elements. Those redundant passes were forced layout
         and style-attribute writes landing in exactly the frames the switch
         animation needed, which is the half of the bar a shopper feels.
         Read the cheap signature first and only pay for the fit when the
         inputs have actually moved; the convergence semantics below are
         untouched, because the same signature still decides stability. */
      var pre = geomSig();
      var dirty = groupsAll().some(function (g) { return g.__rpgDirty; });
      if (pre !== last || dirty) fitAll();
      var sig = pre !== last || dirty ? geomSig() : pre;
      if (sig === last) stable += 1; else stable = 0;
      last = sig;
      if (stable >= 3 || ++ticks > 40) clearInterval(settleTimer);
    }, 50);
  }

  /* =====================================================================
     MEASUREMENT CONVERGENCE — the fit must not depend on WHEN it was read.
     ---------------------------------------------------------------------
     maxNat is a FLEET max: every product's card is scaled by the tallest
     card in the shop, so switching product never re-scales the stage. That
     makes the scale depend on products the shopper is not looking at, and
     those are measured in whatever state of hydration the excursion caught
     them in. Measured on staging at the reference viewport, product
     8512208863386 read 948px on some loads and 998px on others, and
     9046928425114 read 888 or 938 - the same 50px row, present or not.
     Nothing invalidated afterwards, so the early reading was final.

     Consequence: k came out 928/948 = 0.979 on some loads and 928/998 =
     0.930 on others - a 5% difference in the size of the card, on the same
     screen, decided by a race. Under the stage law that error is then
     MULTIPLIED by s, so at 4K it is ~80px of visible size difference
     between two loads, and because the whole geometry suite is judged
     against the reference render it also made every other shape read 5%
     wrong whenever the reference happened to catch the early number.

     settle() could never fix this: it re-APPLIES on a timer but nothing in
     it is dirty, so measureLevel returns the cached value and the stale
     reading is re-applied forever.

     This re-MEASURES on a bounded, decelerating schedule and stops when two
     consecutive fleet readings agree. A handful of excursions at most, all
     of them at open time, none of them during interaction. It is not a
     timer hoping to be late enough: a disagreement is what schedules the
     next attempt, and agreement is what ends it.

     TWO CORRECTIONS, both from watching it fail on staging.

     (1) THE SIGNATURE HAS TO INCLUDE WHO IS IN THE FLEET, NOT JUST HOW TALL
     THEY ARE. The shop's product sections are inserted PROGRESSIVELY, not
     all at once: instrumented at 2592x1676, the fitter saw 2 groups at
     t+3.53s, 4 at t+3.81s and all 8 at t+5.11s. A signature that is only a
     list of heights reads two consecutive measurements of a two-group fleet
     as agreement, so the schedule congratulated itself and stopped on a
     fleet that was a quarter assembled. Because the scale is a fleet MAX,
     the tallest card in the shop had simply not arrived yet - and the run
     that produced 26/33 instead of 31/33 is exactly this: maxNat cached at
     948 against a true 998, so k came out 1.4684 where the stage law wants
     1.3948, and every card, every type size and every fraction at that one
     shape read 5.3% too large. Counting the fleet makes an incomplete fleet
     a disagreement, which is what it is.

     (2) A TIMER CANNOT BE THE ONLY MECHANISM, BECAUSE HYDRATION IS AN EVENT.
     See the observer below. The schedule stays as the belt - it is bounded,
     it costs nothing once quiet, and it covers content that changes size
     without changing the DOM (a webfont swap, an image decoding).

     The delays are gaps BETWEEN attempts, so the four of them sit at 0.4s,
     1.4s, 3.2s and 6.0s after arming, and the later two only ever run if
     the fleet is still disagreeing with itself. Measured on staging after
     this change, three consecutive full captures agreed to every digit at
     all four shapes, so in practice it settles on the second. */
  var CONVERGE_DELAYS = [400, 1000, 1800, 2800];
  var convergeTimers = [];

  function fleetSig() {
    var gs = groupsAll();
    /* The COUNT leads, so a fleet that is still assembling can never read as
       a settled one. */
    return gs.length + '#' + gs.map(function (g) {
      return (g.__rpgPaneNats && g.__rpgPaneNats[0] && g.__rpgPaneNats[0][0]) || 0;
    }).join(',');
  }

  function converge() {
    convergeTimers.forEach(clearTimeout);
    convergeTimers = [];
    var last = null;
    var attempt = 0;
    function run() {
      /* Never re-measure through a live height animation: the excursion
         mutes transitions, and an unmute paired against a restored value is
         exactly what cancelled the card's ease mid-flight (Aug 22). */
      var lag = animGuardUntil - performance.now();
      if (lag > 0) { convergeTimers.push(setTimeout(run, lag + 60)); return; }
      invalidate();
      fitAll();
      var sig = fleetSig();
      var agreed = sig === last;
      last = sig;
      attempt += 1;
      if (!agreed && attempt < CONVERGE_DELAYS.length) {
        convergeTimers.push(setTimeout(run, CONVERGE_DELAYS[attempt]));
        return;
      }
      /* TWO READINGS THAT AGREE IS WHAT "THE FLEET IS WHOLE" MEANS, and it is
         the only honest definition available: the count leads the signature,
         so a fleet that is still assembling can never agree with itself. This
         is the moment the panel's `final` scale becomes a fact rather than a
         hope, so it is published rather than kept private - the reveal gate
         below consumes it, and so does the director. */
      /* Announced on EITHER terminal outcome. Agreement is the good one; a
         ladder that ran out without agreeing is still the end of what this
         mechanism can learn, and holding the reveal on a fleet that will
         never agree would be a hang rather than a fit. */
      announceFleetComplete();
    }
    fleetSettled = false;
    convergeTimers.push(setTimeout(run, CONVERGE_DELAYS[0]));
  }

  /* FLEET-COMPLETE IS ONE OF THE DIRECTOR'S EVENTS.
     A late-hydrating fleet re-asserts the current beat, so every owner gets
     the composition again with the settled numbers underneath it. Re-asserting
     is safe by construction: the fitter's own applier returns early when the
     preroll it would start is already running, so this cannot recurse. */
  function announceFleetComplete() {
    if (fleetSettled) return;
    fleetSettled = true;
    (window.HDLOG || function () {})('[HD-FIT] fleet-complete sig', fleetSig(),
      'maxNat', cache.maxNat[0], 'at', Math.round(performance.now()));
    groupsAll().forEach(function (g) {
      if (g.hasAttribute('data-rpg-awaiting-fit')) commitFirstOpen(g);
    });
    try {
      document.dispatchEvent(new CustomEvent('hound:fleet-complete'));
    } catch (error) {}
    /* NO BEAT RE-ASSERT HERE. Re-applying the beat re-runs every owner's
       applier, and this fitter's applier starts a sweep, which converges,
       which announces, which re-asserts: a cycle with a period of a few
       frames. Fleet-complete is published as an event that anything may
       subscribe to (`hound:fleet-complete`), and the one thing that has to act
       on it - the reveal gate - is called directly above. */
  }

  /* =====================================================================
     WHAT MOVES THE FLEET READING IS A NODE ARRIVING, SO WATCH FOR NODES
     ARRIVING.
     ---------------------------------------------------------------------
     Every source of measurement drift in this shop is a DOM INSERTION: a
     product section rendering late, Shopify's accelerated-checkout element
     replacing the empty buy block (85px of card height, and there is no
     iframe to hang a load listener on any more - watchPayButtons below
     finds nothing on this theme), a metafield block arriving. Those are
     events. Racing them with a timer is guesswork; the browser will simply
     tell us.

     THE OBSERVER LOOP THIS FILE HAS BEEN BITTEN BY TWICE CANNOT HAPPEN
     HERE, and the reason is structural rather than a guard. The August rule
     is "never observe a tree you also mutate", and it retired a
     ResizeObserver because an async callback cannot be gated by a
     synchronous flag. This observes childList ONLY. The fitter's entire
     write surface is attributes and inline styles - data-rpg-measuring,
     data-rpg-compact, --rpg-card-k/-shed/-nat, section display, and the
     .active class on panes. It does not add or remove a single node
     anywhere, so it cannot deliver itself a mutation record. The
     measuringNow check below is belt, not braces.

     The gallery is deliberately out of range: it is a SIBLING of the modal
     group inside the section, not a descendant, so the media-promotion
     machinery that moves video nodes around during a product switch never
     reaches this. Media height is already handled where it belongs, on
     loadedmetadata/resize. */
  var contentTimer = null;
  var contentPending = null;   /* 'fleet' | an element | null */

  function flushContentChange() {
    /* Same rule the convergence schedule follows: never re-measure through a
       live height animation. */
    var lag = animGuardUntil - performance.now();
    if (lag > 0) { contentTimer = setTimeout(flushContentChange, lag + 60); return; }
    var target = contentPending;
    contentPending = null;
    if (target === 'fleet') invalidate(); else if (target) invalidateFor(target);
    schedule();
  }

  function noteContentChange(what) {
    /* A fleet-level change subsumes any pending local one. */
    contentPending = (what === 'fleet' || contentPending === 'fleet') ? 'fleet' : what;
    clearTimeout(contentTimer);
    contentTimer = setTimeout(flushContentChange, 160);
  }

  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function (recs) {
      if (measuringNow) return;
      var verdict = null;
      for (var i = 0; i < recs.length && verdict !== 'fleet'; i++) {
        var m = recs[i];
        var lists = [m.addedNodes, m.removedNodes];
        for (var l = 0; l < 2; l++) {
          for (var j = 0; j < lists[l].length; j++) {
            var n = lists[l][j];
            if (!n || n.nodeType !== 1) continue;
            /* A whole product arriving or leaving changes WHO is measured,
               and the scale is a fleet max, so the whole cache is stale. */
            if ((n.matches && n.matches('.product-modal-group')) ||
                (n.querySelector && n.querySelector('.product-modal-group'))) {
              verdict = 'fleet';
              break;
            }
            /* A row arriving inside a card that already exists changes only
               THAT card's natural height. */
            var host = m.target && m.target.closest
              ? m.target.closest('.product-modal-group') : null;
            if (host && verdict === null) verdict = host;
          }
          if (verdict === 'fleet') break;
        }
      }
      if (verdict) noteContentChange(verdict);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* Arm convergence only on the events that genuinely change what the fleet
     measures - a view opening, a resize, fonts landing, the page finishing.
     Never on a click: a tab or product switch must not re-open a race the
     card has already settled out of. */
  function scheduleFull() { schedule(); converge(); }

  var pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      /* rAF callbacks run BEFORE the frame paints. Fitting here means a card
         revealed in this same frame (selector click, view navigate) is
         measured and scaled before it is ever painted. Deferring everything
         to the settle interval - which first fires ~50ms AFTER the paint -
         is what showed one frame of the un-fitted card: full layout height,
         over the header, then a visible snap to size ("blows up on the
         right before glitching back", Aug 22). */
      fitAll();
      settle();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleFull);
  } else {
    scheduleFull();
  }
  window.addEventListener('resize', function () { invalidate(); scheduleFull(); });
  window.addEventListener('orientationchange', function () { invalidate(); scheduleFull(); });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { invalidate(); scheduleFull(); });
  }

  document.addEventListener('navigate', scheduleFull);
  document.addEventListener('parallax:enter', scheduleFull);
  /* Tab and product-selector clicks refresh the overflow signal; with the
     global measurement they can no longer change the scale, which is the
     point. Variant/gender clicks can change content height, so those
     invalidate. */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    /* Selector clicks invalidate too: a hidden product's video is unloaded
       (preload=none), so its gallery measured short in the excursion and the
       real height only exists once the product is opened and the video has
       dimensions. */
    var selectorButton = e.target.closest('.product-selector-button[data-product-id]');
    if (selectorButton) {
      /* Product switch. Two traps found live (Aug 23, the Thorn card's 220px
         empty slab): the old matcher named a class that does not exist
         ('.product-selector-button'), so armoury clicks NEVER re-measured and
         every card kept the height its hidden, UN-hydrated state measured -
         the notify-me form renders tall until its JS collapses it to a
         trigger. And that collapse happens asynchronously after the section
         opens, so one click-time measure is not enough: re-check after the
         hydration window. */
      /* PARTIAL, not full: a full invalidate here re-measured the entire
         fleet (every section revealed, three pane passes) three times per
         product switch - hundreds of ms of synchronous layout right as the
         product video starts playing. Discord users reported the video
         "freezing upon switching" (Aug 23). The theme's own handler has
         already revealed the target section by the time this listener runs,
         so the VISIBLE group is the one that needs fresh numbers. */
      if (selectorRecheckRaf) cancelAnimationFrame(selectorRecheckRaf);
      selectorRecheckTimers.forEach(clearTimeout);
      selectorRecheckTimers = [];
      var productId = selectorButton.getAttribute('data-product-id');
      var markProduct = function () {
        var vis = groupsAll().filter(function (group) {
          if (!isShowing(group)) return false;
          var info = group.querySelector('[data-product-id]');
          return info && info.getAttribute('data-product-id') === productId;
        })[0];
        if (!vis) return;
        vis.__rpgDirty = true;
        schedule();
      };
      selectorRecheckRaf = requestAnimationFrame(markProduct);
      selectorRecheckTimers.push(setTimeout(markProduct, 900));
      selectorRecheckTimers.push(setTimeout(markProduct, 2400));
    }
    else if (e.target.closest('.product-modal-group label, .gender-toggle-btn')) { invalidateFor(e.target); schedule(); }
    else if (e.target.closest('.product-info-tab-button')) {
      var tg = e.target.closest('[id^="shopify-section-"]');
      var tgg = tg && tg.querySelector('.product-modal-group');
      if (tgg) {
        tgg.classList.add('rpg-pane-incoming');
        /* If the new pane needs no height change there is no transitionend;
           reveal after the transition's own duration regardless. */
        setTimeout(function () { tgg.classList.remove('rpg-pane-incoming'); }, 420);
      }
      schedule();
    }
  });
  /* NO ResizeObserver. It was tried and removed twice over: observing the
     card's children (and later the galleries) created feedback loops that no
     guard could reliably break - RO delivery is async, so a synchronous
     "measuring" flag is stale by delivery time, a time-window flag races the
     frame scheduler under load, and one group that kept re-qualifying turned
     every settle tick into a measuring excursion whose transition-mute
     CANCELLED the in-flight height animation (traced live: transitionrun at
     129ms, excursions at 146/330ms, transitioncancel at 315ms). The honest
     invalidation sources are enumerable and none of them feed back:
       - viewport resize / orientation / fonts.ready  -> full sweep
       - variant, gender, product-selector clicks     -> that section only
       - a video getting its dimensions               -> that section only */
  function watchVideos() {
    Array.prototype.forEach.call(document.querySelectorAll('[id*="__rpg_shop_"] video'), function (v) {
      if (v.__rpgWatched) return;
      v.__rpgWatched = true;
      var onDims = function () {
        /* A HIDDEN SECTION'S VIDEO GETTING ITS DIMENSIONS IS NOT A REASON TO
           RE-FIT THE CARD SOMEBODY IS LOOKING AT.
           -----------------------------------------------------------------
           The den now warms off-stage product clips in the background, and
           load() on any of them fires loadedmetadata/resize exactly like a
           real hydration does. Every one of those was scheduling a full fleet
           fit over the stage: den-idle.py caught it as 24 excursion attribute
           writes, 24 inline style rewrites and a spinner toggle during a
           12-second idle window - intermittently, because it only lands if a
           warm happens to complete inside the sample.
           Mark the section dirty anyway, which is true and costs nothing, and
           let the fit run when that section is actually staged: the product
           selector calls HoundShopFit.fit() twice as part of every commit,
           and a dirty section is re-measured then. */
        var fire = function () {
          invalidateFor(v);
          var vSec = v.closest ? v.closest('[id^="shopify-section-"]') : null;
          var offStage = !!vSec && (vSec.style.display === 'none' ||
            getComputedStyle(vSec).display === 'none' || vSec.offsetParent === null);
          if (!offStage) schedule();
        };
        /* arriving mid-animation: wait the animation out instead of letting
           the excursion's transition-mute cancel it */
        var lag = animGuardUntil - performance.now();
        if (lag > 0) setTimeout(fire, lag + 50); else fire();
      };
      v.addEventListener('loadedmetadata', onDims);
      v.addEventListener('resize', onDims);
    });
  }
  watchVideos();
  document.addEventListener('navigate', watchVideos);
  /* Product sections are now hydrated on demand. Their <video> nodes do not
     exist during this script's initial watchVideos() pass, and hydration is
     not a navigation, so the old hooks never attached. The first gallery fit
     therefore kept the poster-era measurement even after loadedmetadata
     changed the real video box (10px past the fold at 1434x767). Attach before
     the selector promotes the hydrated video to preload=auto, then dirty only
     that section so its final dimensions produce one honest gallery refit. */
  document.addEventListener('hound:section-hydrated', function (event) {
    watchVideos();
    var section = event.detail && event.detail.element;
    if (section) invalidateFor(section); else invalidate();
    schedule();
  });

  /* Shopify's payment button hydrates as an iframe well after first fit and
     adds real height to the card. Without a re-fit the content outgrew the
     measured lock and the animation clip cut it flat at the stone's bottom
     edge. */
  function watchPayButtons() {
    Array.prototype.forEach.call(document.querySelectorAll('[id*="__rpg_shop_"] [data-shopify-buttoncontainer] iframe, [id*="__rpg_shop_"] .shopify-payment-button iframe'), function (f) {
      if (f.__rpgWatched) return;
      f.__rpgWatched = true;
      f.addEventListener('load', function () { invalidateFor(f); schedule(); });
    });
  }
  watchPayButtons();
  document.addEventListener('navigate', watchPayButtons);
  window.addEventListener('load', function () { watchPayButtons(); invalidate(); scheduleFull(); });

  window.HoundShopFit = {
    fit: fitAll, invalidate: invalidate, invalidateFor: invalidateFor,
    /* The token beside what the shop actually measures, so a catalogue that
       has outgrown REF_CARD_NAT is one console call away rather than a
       silently clipped card. `ref` is the constant the scale is built from;
       `measured` is the sweep's latest fleet reading; `over` names the
       products that exceed the token, which are the ones the elision ladder
       and the clipped-band lock are handling. */
    fleetMax: function () {
      var over = [];
      groupsAll().forEach(function (g) {
        var n = g.__rpgNat0 || 0;
        if (n > REF_CARD_NAT) {
          var sec = g.closest('[id^="shopify-section-"]');
          var t = sec && sec.querySelector('.product__title, h1, h2');
          over.push({ nat: n, name: (t ? t.textContent : '?').trim().slice(0, 40),
                      fit: g.getAttribute('data-rpg-fit'),
                      compact: g.getAttribute('data-rpg-compact') });
        }
      });
      return { ref: REF_CARD_NAT, measured: cache.maxNat[0], over: over };
    }
  };

  /* THE FITTER'S PLACE IN THE TABLE.
     It owns what scale the panel is revealed at, and the director tells it
     WHEN that scale has to be ready: `panel: 'hidden'` at the market beat is
     the cue to have measured already, and `panel: 'final'` at the product beat
     is the promise that follows from it. The preroll is only permitted while
     the panel is hidden - once a card is on stage there is a real box to
     measure and forcing a hidden one open would be measuring the wrong card.

     converge() is the bounded, decelerating re-measure that stops when two
     consecutive fleet readings agree; arming it here is what lets the +50px of
     late hydration land while the shopper is still looking at the street.

     The director may not exist (an older cached layout, a page without it), in
     which case this never registers and the fitter behaves exactly as it did
     before: first fit at first click. */
  if (window.HoundDenDirector && window.HoundDenDirector.register) {
    window.HoundDenDirector.register('shop-fitter', function (row) {
      var inDen = row.frame === 'locked';
      prerollAllowed = inDen && row.panel === 'hidden';
      /* BOTH DEN ROWS GET THE SWEEP. The market row is the obvious one - panel
         hidden, nothing picked up, seconds to spare - but a `?den=` deep link
         commits a product within a frame of entering, so a market-only sweep
         is cancelled before it lays out anything and every later switch pays
         first-open cost. Measured over 18 switches on the deep-link path:
         market-only leaves 400-5000ms stalls on the first visit to each
         product; sweeping at both rows put every switch in 43-131ms. It is
         still a one-time burst - fleetNeedsLayout() below is what stops it
         running again once every section has been laid out once. */
      if (!inDen) { sweepAllowed = false; cancelSweep(); return; }
      /* Drop any sweep in flight first: at the product beat it may be holding
         the very section that has just been committed. cancelSweep leaves an
         adopted section alone (see putFleetAway) and the fresh sweep below
         will not pick it up, because it only takes sections that are
         display:none. */
      sweepAllowed = false;
      cancelSweep();
      sweepAllowed = true;
      /* The market row means: panel hidden, nothing picked up, seconds to
         spare. Spend them making sure nothing in the fleet is cold by the time
         anything is clicked - which is the same fix for the reveal landing on
         an interim scale AND for a first open costing hydration mid-switch. */
      hydrateFleet();
    });
    /* A section arriving while the market is open joins the same sweep. */
    document.addEventListener('hound:section-hydrated', function () {
      if (sweepAllowed) hydrateFleet();
    });
  }
})();
